/**
 * Design tokens for the application theme.
 * Centralizes all color values for consistent theming.
 */
export const tokens = {
  colors: {
    // Brand colors
    primary: '#F06423',
    primaryAlpha: '#f0642394',
    primaryDark: '#F06423',
    secondary: '#f16522',
    warning: '#d32f2f',

    // Text colors
    text: {
      primary: '#080808',
      secondary: '#666666',
      muted: '#999999',
      disabled: 'rgba(0, 0, 0, 0.26)',
    },

    // Background colors
    background: {
      default: '#FFFFFF',
      paper: '#FAFAFA',
      subtle: '#f8f9fa',
      body: '#f0f1f2',
      dark: '#121212',
      darkPaper: '#1e1e1e',
      darkBody: '#0a0a0a',
    },

    // Border colors
    border: {
      light: '#e1e1e1',
      medium: '#CCCCCC',
      default: 'lightgray',
      disabled: 'rgba(0, 0, 0, 0.12)',
    },

    // Button colors
    button: {
      containedBg: '#e0e0e0',
      containedText: 'rgba(0, 0, 0, 0.87)',
    },

    // Common colors
    white: '#FFFFFF',
    black: '#000000',
  },
};

// Light mode palette derived from tokens
export const lightPalette = {
  mode: 'light' as const,
  primary: {
    main: tokens.colors.primary,
    dark: tokens.colors.primaryDark,
  },
  secondary: {
    main: tokens.colors.secondary,
  },
  warning: {
    main: tokens.colors.warning,
  },
  text: {
    primary: tokens.colors.text.primary,
    secondary: tokens.colors.text.secondary,
    disabled: tokens.colors.text.disabled,
  },
  background: {
    default: tokens.colors.background.default,
    paper: tokens.colors.background.paper,
  },
  divider: tokens.colors.border.light,
};

// Dark mode palette derived from tokens
export const darkPalette = {
  mode: 'dark' as const,
  primary: {
    main: tokens.colors.primary, // Orange works well in dark mode too
    dark: tokens.colors.primaryDark,
  },
  secondary: {
    main: tokens.colors.secondary,
  },
  warning: {
    main: tokens.colors.warning,
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#B0B0B0',
    disabled: 'rgba(255, 255, 255, 0.3)',
  },
  background: {
    default: tokens.colors.background.dark,
    paper: tokens.colors.background.darkPaper,
  },
  divider: '#333333',
};

// CSS variable values for light mode
export const lightCssVars = {
  '--color-primary': tokens.colors.primary,
  '--color-primary-alpha': tokens.colors.primaryAlpha,
  '--color-primary-dark': tokens.colors.primaryDark,
  '--color-secondary': tokens.colors.secondary,
  '--color-warning': tokens.colors.warning,
  '--color-text-primary': tokens.colors.text.primary,
  '--color-text-secondary': tokens.colors.text.secondary,
  '--color-text-muted': tokens.colors.text.muted,
  '--color-background': tokens.colors.background.default,
  '--color-paper': tokens.colors.background.paper,
  '--color-background-subtle': tokens.colors.background.subtle,
  '--color-body-background': tokens.colors.background.body,
  '--color-border-light': tokens.colors.border.light,
  '--color-border-medium': tokens.colors.border.medium,
  // Attention/action-needed states (yellow/amber cards)
  '--color-attention-bg': '#fefaed',
  '--color-attention-bg-alt': '#f8f4ea',
  '--color-attention-bg-strong': '#FFECDD',
  '--color-attention-text': tokens.colors.text.primary,
  '--color-attention-border': '#e0ddd2',
  // Highlight color for recommended items
  '--color-highlight': '#F7AC40',
  // Link colors
  '--color-link': '#2D8CFF',
  '--color-link-hover': '#0056b3',
  // Patient name link color (brand-specific)
  '--color-patient-link': '#883A00',
  // Shadow color
  '--color-shadow': 'rgba(0, 0, 0, 0.16)',
  // Recommended card background (medication recommendations)
  '--color-recommended-bg': '#52514F',
  // Reconciliation card colors
  '--color-reconcile-header': '#f1f1f1',
  '--color-reconcile-matched-bg': '#00800052',
  '--color-reconcile-matched-border': '#00800078',
  '--color-reconcile-new-bg': '#e6f7ff',
  '--color-reconcile-new-border': '#99ddff',
  '--color-reconcile-quarantine-bg': '#fba7a7d1',
  '--color-reconcile-quarantine-border': '#f65b5bbf',
  // Override card colors
  '--color-override-bg': '#F1F1F6',
  '--color-override-alt-bg': '#FBF6F6',
  '--color-override-border': '#FFDAB9',
  // Referral card colors
  '--color-referral-bg': '#f8ebeb',
  '--color-referral-border': '#DB2323',
  // Condition evaluation dialog colors
  '--color-eval-card-bg': '#f2f2f2',
  '--color-eval-highlight-bg': '#F0642328',
  '--color-eval-highlight-purple': '#bf65e8',
  '--color-eval-highlight-blue': '#256aef',
  // Status colors (success, error, etc.)
  '--color-success': '#2e7d32',
  '--color-success-light': '#4caf50',
  '--color-success-bg': '#e8f5e9',
  '--color-error': '#d32f2f',
  '--color-error-light': '#ef5350',
  '--color-error-bg': '#ffebee',
  // KB version status dot. Fixed values in both themes: the dot sits on the
  // AppBar, which is #000000 (or #0047AB for NAVIGATOR) regardless of theme,
  // so --color-success (#2e7d32 in light) would be unreadable there.
  '--color-kb-status-matched': '#4caf50',
  '--color-kb-status-mismatched': '#ff5252',
  '--color-kb-status-unknown': '#bdbdbd',
  // Native date input calendar icon filter
  '--calendar-icon-filter': 'none',
  // Primary action button colors (light mode: orange; dark mode: grey — set in darkCssVars)
  '--color-btn-primary-bg': '#F06423',
  '--color-btn-primary-text': '#FFFFFF',
  '--color-btn-primary-border': '#F06423',
  // Disabled/inactive button colors
  '--color-btn-disabled-bg': '#CCCCCC',
  '--color-btn-disabled-text': '#999999',
  '--color-btn-disabled-border': '#CCCCCC',
  // Selected button colors (Approve/Reject buttons)
  '--color-btn-selected-bg': '#3E3D3B',
  '--color-btn-selected-text': '#FFFFFF',
  '--color-btn-selected-border': '#3E3D3B',
  // Task owner role chip colors
  '--color-role-provider-bg': 'rgba(156, 39, 176, 0.12)',
  '--color-role-provider-text': '#7b1fa2',
  '--color-role-navigator-bg': 'rgba(0, 150, 136, 0.12)',
  '--color-role-navigator-text': '#00695c',
  // Info/blue badge (Preferred pharmacy, Primary provider, test-rank chip)
  '--color-badge-info-bg': '#e3f2fd',
  '--color-badge-info-text': '#1565c0',
  // Purple surface (sequential-test paired rows)
  '--color-badge-purple-bg': '#f3e5f5',
  // Neutral muted surface (light-gray dialog headers/footers, login panels)
  '--color-surface-muted': '#f5f5f5',
  // Selected-state color for 1-5 scale quick-answer buttons
  '--color-answer-scale': '#1976d2',
};

// CSS variable values for dark mode
export const darkCssVars = {
  '--color-primary': tokens.colors.primary, // Orange works in dark mode
  '--color-primary-alpha': tokens.colors.primaryAlpha,
  '--color-primary-dark': tokens.colors.primaryDark,
  '--color-secondary': tokens.colors.secondary,
  '--color-warning': tokens.colors.warning,
  '--color-text-primary': '#FFFFFF',
  '--color-text-secondary': '#B0B0B0',
  '--color-text-muted': '#888888',
  '--color-background': tokens.colors.background.dark,
  '--color-paper': tokens.colors.background.darkPaper,
  '--color-background-subtle': '#2a2a2a',
  '--color-body-background': tokens.colors.background.darkBody,
  '--color-border-light': '#454545',
  '--color-border-medium': '#555555',
  // Attention/action-needed states - brighter amber for dark mode visibility
  '--color-attention-bg': '#5d4a2a',
  '--color-attention-bg-alt': '#54421f',
  '--color-attention-bg-strong': '#6a5028',
  '--color-attention-text': '#fff8e8',
  '--color-attention-border': '#8a7040',
  // Highlight color for recommended items - slightly brighter for dark mode
  '--color-highlight': '#FFB84D',
  // Link colors - lighter blue for better readability in dark mode
  '--color-link': '#64B5F6',
  '--color-link-hover': '#90CAF9',
  // Patient name link color - warmer tone for dark mode
  '--color-patient-link': '#FFB84D',
  // Shadow color - slightly visible against dark background
  '--color-shadow': 'rgba(0, 0, 0, 0.4)',
  // Recommended card background - dark teal for dark mode
  '--color-recommended-bg': '#2D4A4A',
  // Reconciliation card colors - darker variants for dark mode
  '--color-reconcile-header': '#2a2a2a',
  '--color-reconcile-matched-bg': '#1a3a1a',
  '--color-reconcile-matched-border': '#2d5a2d',
  '--color-reconcile-new-bg': '#1a2a3a',
  '--color-reconcile-new-border': '#3a5a7a',
  '--color-reconcile-quarantine-bg': '#4a2020',
  '--color-reconcile-quarantine-border': '#7a3a3a',
  // Override card colors - darker for dark mode
  '--color-override-bg': '#2a2a2f',
  '--color-override-alt-bg': '#2f2a2a',
  '--color-override-border': '#5a4a3a',
  // Referral card colors - darker for dark mode
  '--color-referral-bg': '#3d2020',
  '--color-referral-border': '#DB2323',
  // Condition evaluation dialog colors - darker for dark mode
  '--color-eval-card-bg': '#2a2a2a',
  '--color-eval-highlight-bg': '#F0642350',
  '--color-eval-highlight-purple': '#d69ef0',
  '--color-eval-highlight-blue': '#6b9ef0',
  // Status colors (success, error, etc.) - visible but not too bright for dark mode
  '--color-success': '#4caf50',
  '--color-success-light': '#66bb6a',
  '--color-success-bg': '#1b3d1b',
  '--color-error': '#f44336',
  '--color-error-light': '#e57373',
  '--color-error-bg': '#3d1b1b',
  // KB version status dot -- identical to lightCssVars on purpose; see note there.
  '--color-kb-status-matched': '#4caf50',
  '--color-kb-status-mismatched': '#ff5252',
  '--color-kb-status-unknown': '#bdbdbd',
  // Native date input calendar icon filter - no filter needed, color-scheme handles it
  '--calendar-icon-filter': 'none',
  // Primary action button colors — grey (like "Live Audio") in dark mode
  '--color-btn-primary-bg': '#e0e0e0',
  '--color-btn-primary-text': 'rgba(0, 0, 0, 0.87)',
  '--color-btn-primary-border': '#e0e0e0',
  // Disabled/inactive button colors — matches outlined dark mode style
  '--color-btn-disabled-bg': 'var(--color-background-subtle)',
  '--color-btn-disabled-text': 'var(--color-text-primary)',
  '--color-btn-disabled-border': 'var(--color-border-medium)',
  // Selected button colors - lighter to stand out against dark background
  '--color-btn-selected-bg': '#4CAF50',
  '--color-btn-selected-text': '#FFFFFF',
  '--color-btn-selected-border': '#4CAF50',
  // Task owner role chip colors - brighter for dark mode visibility
  '--color-role-provider-bg': 'rgba(206, 147, 216, 0.2)',
  '--color-role-provider-text': '#ce93d8',
  '--color-role-navigator-bg': 'rgba(128, 203, 196, 0.2)',
  '--color-role-navigator-text': '#80cbc4',
  // Info/blue badge — dark surface so inherited/light text reads in dark mode
  '--color-badge-info-bg': '#15324a',
  '--color-badge-info-text': '#90caf9',
  // Purple surface — dark variant so white row text reads in dark mode
  '--color-badge-purple-bg': '#3a2a45',
  // Neutral muted surface — dark gray in dark mode
  '--color-surface-muted': '#2a2a2a',
  // Selected-state color for 1-5 scale quick-answer buttons — brighter blue
  '--color-answer-scale': '#64b5f6',
};
